import { Box, Chip, Sheet, Stack, Typography } from '@mui/joy';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

/**
 * Предпросмотр креатива в том виде, в каком его увидит пользователь.
 *
 * Используется в трёх местах: карточка кампании (Ф1), мастер создания (Ф2)
 * и очередь модерации в админке (Ф3). Модератор должен принимать решение,
 * глядя на объявление, а не на таблицу его полей.
 *
 * Пометка «Реклама» здесь не декоративная — по закону она обязана быть
 * на реальном блоке, и в предпросмотре её тоже надо видеть.
 */

const AD_MARK_SX = {
    position: 'absolute', top: 8, left: 8, zIndex: 3,
    bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
    px: 0.8, py: 0.2, borderRadius: 'sm',
    fontSize: 10, letterSpacing: 0.4,
};


function AdMark() {
    return <Box sx={AD_MARK_SX}>РЕКЛАМА</Box>;
}

function Placeholder({ label, ratio = '3 / 4' }) {
    return (
        <Box sx={{
            aspectRatio: ratio, width: '100%',
            bgcolor: 'neutral.softBg', color: 'neutral.500',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 'sm',
        }}>
            <Typography level="body-xs">{label}</Typography>
        </Box>
    );
}

function CtaPill({ text }) {
    return (
        <Box sx={{
            bgcolor: 'primary.500', color: '#fff',
            px: 1.5, py: 0.6, borderRadius: 'md',
            fontSize: 13, fontWeight: 600, display: 'inline-block',
        }}>
            {text || 'Подробнее'}
        </Box>
    );
}


function DeckCardPreview({ c }) {
    return (
        <Box sx={{
            position: 'relative', width: 240, borderRadius: 'lg',
            overflow: 'hidden', boxShadow: 'md', bgcolor: 'background.surface',
        }}>
            <AdMark />
            <Box sx={{ position: 'relative', aspectRatio: '3 / 4', bgcolor: 'neutral.softBg' }}>
                {c.image_url
                    ? <Box component="img" src={c.image_url} alt=""
                           sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Placeholder label="Изображение 3:4" />}
                <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,.75) 0%, rgba(0,0,0,0) 55%)',
                }} />
                <Box sx={{ position: 'absolute', bottom: 0, p: 1.5, color: '#fff' }}>
                    <Typography level="title-sm" sx={{ color: '#fff' }}>
                        {c.title || 'Заголовок объявления'}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,.85)', mb: 1 }}>
                        {c.text || 'Текст объявления'}
                    </Typography>
                    <CtaPill text={c.cta_text} />
                </Box>
            </Box>
            <Box sx={{ px: 1.5, py: 0.8 }}>
                <Typography level="body-xs" color="neutral">
                    {c.brand_name || 'Рекламодатель не указан'}
                </Typography>
            </Box>
        </Box>
    );
}


function BannerBottomPreview({ c }) {
    return (
        <Sheet variant="outlined" sx={{
            position: 'relative', width: 320, borderRadius: 'md',
            p: 1, display: 'flex', gap: 1, alignItems: 'center',
        }}>
            <Box sx={{
                width: 48, height: 48, borderRadius: 'sm', flexShrink: 0,
                overflow: 'hidden', bgcolor: 'neutral.softBg',
            }}>
                {c.image_url && (
                    <Box component="img" src={c.image_url} alt=""
                         sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography level="body-sm" noWrap sx={{ fontWeight: 600 }}>
                    {c.title || 'Заголовок'}
                </Typography>
                <Typography level="body-xs" color="neutral" noWrap>
                    {c.brand_name || 'Рекламодатель'} · Реклама
                </Typography>
            </Box>
            <CtaPill text={c.cta_text} />
        </Sheet>
    );
}


function FullscreenPreview({ c }) {
    return (
        <Box sx={{
            position: 'relative', width: 200, aspectRatio: '9 / 16',
            borderRadius: 'lg', overflow: 'hidden', boxShadow: 'md',
            bgcolor: 'neutral.softBg',
        }}>
            <AdMark />
            <Box sx={{
                position: 'absolute', top: 8, right: 8, zIndex: 3,
                width: 22, height: 22, borderRadius: '50%',
                bgcolor: 'rgba(0,0,0,.5)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
            </Box>
            {c.image_url
                ? <Box component="img" src={c.image_url} alt=""
                       sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Placeholder label="Изображение 9:16" ratio="9 / 16" />}
            <Box sx={{
                position: 'absolute', bottom: 0, width: '100%', p: 1.5,
                background: 'linear-gradient(to top, rgba(0,0,0,.8), transparent)',
                color: '#fff',
            }}>
                <Typography level="title-sm" sx={{ color: '#fff' }}>
                    {c.title || 'Заголовок'}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,.85)', mb: 1 }}>
                    {c.text || 'Текст объявления'}
                </Typography>
                <CtaPill text={c.cta_text} />
            </Box>
        </Box>
    );
}


function RewardedVideoPreview({ c }) {
    return (
        <Box sx={{
            position: 'relative', width: 200, aspectRatio: '9 / 16',
            borderRadius: 'lg', overflow: 'hidden', boxShadow: 'md',
            bgcolor: 'neutral.700',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <AdMark />
            {c.image_url && (
                <Box component="img" src={c.image_url} alt=""
                     sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                           objectFit: 'cover', opacity: 0.6 }} />
            )}
            <Stack alignItems="center" spacing={1} sx={{ position: 'relative', color: '#fff' }}>
                <PlayCircleFilledRoundedIcon sx={{ fontSize: 48 }} />
                <Typography level="body-xs" sx={{ color: '#fff' }}>
                    {c.video_duration_sec ? `${c.video_duration_sec} сек` : 'Видео'}
                </Typography>
                <Chip size="sm" variant="solid" color="primary">Награда после просмотра</Chip>
            </Stack>
        </Box>
    );
}


function FeedNativePreview({ c }) {
    return (
        <Sheet variant="outlined" sx={{ width: 300, borderRadius: 'md', overflow: 'hidden' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.softBg' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="body-xs" noWrap sx={{ fontWeight: 600 }}>
                        {c.brand_name || 'Рекламодатель'}
                    </Typography>
                </Box>
                <Chip size="sm" variant="soft" color="neutral">Промо</Chip>
            </Stack>
            <Box sx={{ aspectRatio: '16 / 9', bgcolor: 'neutral.softBg' }}>
                {c.image_wide_url || c.image_url
                    ? <Box component="img" src={c.image_wide_url || c.image_url} alt=""
                           sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Placeholder label="Изображение 16:9" ratio="16 / 9" />}
            </Box>
            <Box sx={{ p: 1.2 }}>
                <Typography level="title-sm">{c.title || 'Заголовок'}</Typography>
                <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                    {c.text || 'Текст объявления'}
                </Typography>
                <CtaPill text={c.cta_text} />
            </Box>
        </Sheet>
    );
}


function ChatListPreview({ c }) {
    return (
        <Sheet variant="outlined" sx={{
            width: 320, borderRadius: 'md', p: 1,
            display: 'flex', gap: 1.2, alignItems: 'center',
        }}>
            <Box sx={{
                width: 44, height: 44, borderRadius: '50%',
                overflow: 'hidden', bgcolor: 'neutral.softBg', flexShrink: 0,
            }}>
                {c.image_url && (
                    <Box component="img" src={c.image_url} alt=""
                         sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={0.6} alignItems="center">
                    <Typography level="body-sm" noWrap sx={{ fontWeight: 600 }}>
                        {c.title || 'Заголовок'}
                    </Typography>
                    <Chip size="sm" variant="soft" color="neutral">Реклама</Chip>
                </Stack>
                <Typography level="body-xs" color="neutral" noWrap>
                    {c.text || 'Короткий текст'}
                </Typography>
            </Box>
        </Sheet>
    );
}


const RENDERERS = {
    deck_card: DeckCardPreview,
    search_inline: DeckCardPreview,
    banner_bottom: BannerBottomPreview,
    fullscreen: FullscreenPreview,
    app_open: FullscreenPreview,
    rewarded_video: RewardedVideoPreview,
    feed_native: FeedNativePreview,
    profile_bottom: FeedNativePreview,
    chat_list: ChatListPreview,
};


export default function AdCreativePreview({ creative, format }) {
    const c = creative || {};
    const Renderer = RENDERERS[format || c.format] || DeckCardPreview;
    return <Renderer c={c} />;
}